import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { AccountAppValue } from "@fabric-types/app-account";
import { PlatformServiceCommand } from "@fabric-types/svc-platform";
import { Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { SidenavService } from "@obg/core.sidenav";
import {
	ANNUAL_DATA_VALIDATION_ALERT_DIALOG_ID,
	AnnualDataValidationAlertContainer,
	CAFAuthenticatorService,
} from "@obg/mat.ui.common";
import { AlertButtonStyle, TranslatableAlertService, AlertButtonColor, DialogService } from "@obg/mat.ui.core";
import { CommonState } from "@obg/ngx.common";
import {
	AbstractAlertButton,
	AllowedActions,
	CustomerManagementSelector,
	DisallowedActionCodes,
	Jurisdiction,
	RequiredAction,
	RequiredActions,
	CORE_ROUTE,
	KycState,
} from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class IdentityVerificationGuard {
	constructor(
		private cafAuthenticatorService: CAFAuthenticatorService,
		private translatableAlertService: TranslatableAlertService,
		private dialogService: DialogService,
		private store: Store<CommonState>,
		private documentRef: DocumentRef,
		private customerSupportService: CustomerSupportService,
		private sidenavService: SidenavService
	) {}

	canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
		return combineLatest([
			this.store.select(CustomerManagementSelector.getAllowedAction),
			this.store.select(CustomerManagementSelector.getRequiredActions),
			this.store.select(CustomerManagementSelector.getKYCStatus),
		]).pipe(
			take(1),
			tap(([allowedActions, requiredActions, kycState]) => {
				if (!allowedActions) {
					return;
				}

				if (allowedActions[AllowedActions.Withdraw].isAllowed) {
					this.sidenavService.closeAll();
					return;
				}

				this.showPaymentBlocked(
					allowedActions[AllowedActions.Withdraw].disallowedReasons,
					requiredActions,
					kycState ?? KycState.None
				);
			}),
			switchMap(([allowedActions]) => {
				if (!allowedActions) {
					return of(false);
				}

				if (allowedActions[AllowedActions.Withdraw].isAllowed) {
					return this.showWithdrawal$(_route, _state);
				}

				return of(false);
			})
		);
	}

	private showWithdrawal$(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
		return new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.AuthenticationActions)).pipe(
			switchMap(({ authenticationActions }) => {
				const shouldRunFacialRecognition = authenticationActions.find(
					(action) =>
						action.customerActionType === "Withdrawal" &&
						action.authenticationActionType === "FacialRecognition"
				);

				return shouldRunFacialRecognition ? this.getToken$() : of(true);
			})
		);
	}

	private getToken$() {
		return this.cafAuthenticatorService.openCafAuthenticator$().pipe(
			map((token) => {
				if (token) {
					this.documentRef.obgEventBus?.dispatch(PlatformServiceCommand.SetItemInStorage, {
						key: `${Jurisdiction.BRA}/authToken`,
						item: token,
						options: {
							type: "memory",
						},
					});
				}
				return !!token;
			})
		);
	}

	private showPaymentBlocked(
		disallowedReasons: DisallowedActionCodes[],
		requiredActions: RequiredAction[] | undefined,
		kycStatus: KycState
	) {
		const isDocumentProcessing =
			disallowedReasons.includes(DisallowedActionCodes.M_CURRENTCUSTOMER_DOCUMENTVERIFICATIONPROCESSING) ||
			(kycStatus === KycState.None &&
				!requiredActions?.some((action) => action.actionType === RequiredActions.DocumentUpload));

		const requiredExpiredAction = this.requiredExpiredAction(requiredActions);

		this.translatableAlertService.open(
			{
				variant: "alternate",
				verticalButtons: true,
				id: "payment-withdraw",
				title: "payment.withdraw-not-allowed-title",
				message: this.generateText(requiredExpiredAction, isDocumentProcessing),
				hideClose: true,
				dialogConfig: {
					disableClose: true,
					closeOnNavigation: true,
				},
				mustBeInteracted: true,
				buttons: this.generateButtons(requiredExpiredAction, isDocumentProcessing),
			},
			[]
		);
	}

	private requiredExpiredAction(requiredActions: RequiredAction[] | undefined): RequiredActions | undefined {
		if (!requiredActions?.length) {
			return undefined;
		}

		let annualExpiration: string;
		let documentExpiration: string;

		let isAnnualExpired = false;
		let isDocumentExpired = false;

		const isDocumentRequired = requiredActions.some(
			(action) => action.actionType === RequiredActions.DocumentUpload
		);

		const isAnnualRequired = requiredActions.some(
			(action) => action.actionType === RequiredActions.AccountDataValidation
		);

		if (isAnnualRequired) {
			annualExpiration = requiredActions.find(
				(action) => action.actionType === RequiredActions.AccountDataValidation
			)!.activeFrom;

			isAnnualExpired = new Date(annualExpiration) < new Date();
		}

		if (isDocumentRequired) {
			documentExpiration = requiredActions.find(
				(action) => action.actionType === RequiredActions.DocumentUpload
			)!.activeFrom;

			isDocumentExpired = new Date(documentExpiration) < new Date();
		}

		if (isAnnualExpired && isDocumentExpired) {
			if (annualExpiration! <= documentExpiration!) {
				return RequiredActions.AccountDataValidation;
			}

			return RequiredActions.DocumentUpload;
		}

		if (isAnnualExpired) {
			return RequiredActions.AccountDataValidation;
		}

		if (isDocumentExpired) {
			return RequiredActions.DocumentUpload;
		}

		return undefined;
	}

	private generateButtons(
		requiredAction: RequiredActions | undefined,
		isDocumentProcessing: boolean
	): AbstractAlertButton[] {
		const acceptButtonStyle: AbstractAlertButton = {
			text: "",
			color: AlertButtonColor.Accent,
			style: AlertButtonStyle.Flat,
		};

		const closeButton = {
			color: AlertButtonColor.Primary,
			style: AlertButtonStyle.Stroked,
			text: "general.cancel",
			handler: () => this.closeDialog(),
		};

		if (isDocumentProcessing) {
			return [closeButton];
		}

		if (requiredAction === RequiredActions.AccountDataValidation) {
			const annualValidationButton = {
				...acceptButtonStyle,
				text: "account-details.annual-data-validation.expired.cta",
				handler: () => {
					this.openAnnualVerification();
					this.closeDialog();
				},
			};

			return [annualValidationButton, closeButton];
		}

		if (requiredAction === RequiredActions.DocumentUpload) {
			const documentUploadButton = {
				...acceptButtonStyle,
				text: "account-details.document-validation.expired.cta",
				handler: () => {
					this.navigateToDocumentUpload();
					this.closeDialog();
				},
			};

			return [documentUploadButton, closeButton];
		}

		const customerSupportButton = {
			...acceptButtonStyle,
			text: "general.contact-customer-support",
			handler: () => {
				this.customerSupportService.openSupportPageInNewTab();
				this.closeDialog();
			},
		};

		return [customerSupportButton, closeButton];
	}

	private generateText(requiredAction: RequiredActions | undefined, isDocumentProcessing: boolean): string {
		if (isDocumentProcessing) {
			return "payment.withdraw-is-processing";
		}

		if (requiredAction === RequiredActions.AccountDataValidation) {
			return "account-details.annual-data-validation.expired.label";
		}

		if (requiredAction === RequiredActions.DocumentUpload) {
			return "account-details.document-validation.expired.label";
		}

		return "payment.withdraw-not-allowed-message";
	}

	private closeDialog() {
		this.translatableAlertService.close();
	}

	private navigateToDocumentUpload() {
		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.responsibleGaming.documentVerification));
	}

	private openAnnualVerification() {
		this.dialogService.openComponent(AnnualDataValidationAlertContainer, {
			id: ANNUAL_DATA_VALIDATION_ALERT_DIALOG_ID,
			disableClose: true,
			closeOnNavigation: false,
		});
	}
}
